import React, { useEffect, useState, useRef, useContext } from "react";
import { connect } from "react-redux";
import MGrid, { ACTION_ADD, MGridColumnDefinition } from "components/Shared/MGrid";
import {
  DATA_TYPE_STRING,
  DATA_TYPE_BUTTON,
  DATA_TYPE_NUMBER,
} from "typeConstants/dataTypes";
import { CustomerDetailsDto, SurveyTypeListDto, CustomerSurveyTypeStampDto } from "interfaces/Mueller.Business.Models";
import { DialogContext } from "components/Shared/DialogProvider";
import { userHasRight } from "selectors/rights";
import { CAN_EDIT_STAMPS } from "typeConstants/rights";
import { 
  getSurveyTypeStamps, 
  addSurveyTypeStamp, 
  removeSurveyTypeStamp, 
  editSurveyTypeStamp 
} from "requests/customerSurveyTypeStamps";
import AddEditStamp from "./AddEditStamp";

interface CustomerSurveyTypeStampsProps {
  verticalDisplay: boolean;
  surveyTypeCodeOverride?: string,  // Optional to override selectedSurveyType prop from redux
  // Redux
  selectedSurveyType: SurveyTypeListDto;
  customerDetails: CustomerDetailsDto;
  canEdit: boolean;
}

const CustomerSurveyTypeStamps = (props: CustomerSurveyTypeStampsProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stamps, setStamps] = useState<CustomerSurveyTypeStampDto[]>([]);
  const dialog = useContext(DialogContext);
  const mounted = useRef(true);

  const surveyTypeCode = props.surveyTypeCodeOverride ? props.surveyTypeCodeOverride : props.selectedSurveyType?.code;

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    getStamps();
  },[props.customerDetails, props.selectedSurveyType, props.surveyTypeCodeOverride]);

  const getStamps = () => {
    setIsLoading(true);
    const handleStamps = (stamps: CustomerSurveyTypeStampDto[]) => {
      setStamps(stamps);
      setIsLoading(false);
    };
    getSurveyTypeStamps(props.customerDetails.number, surveyTypeCode, handleStamps, mounted);
  };

  // Grid Columns

  const columns: MGridColumnDefinition[] = [
    { 
      key: "stamp-text", 
      name: "Stamp", 
      dataSource: "text",
      dataType: DATA_TYPE_STRING,
    },
    { 
      key: "stamp-sort", 
      name: "Sort", 
      dataSource: "sortOrder",
      dataType: DATA_TYPE_NUMBER,
      width: 50,
    },
  ];
  const EDIT_COLUMN_KEY = "EDIT_COLUMN_KEY";
  const DELETE_COLUMN_KEY = "DELETE_COLUMN_KEY";
  const editButton = () => <i className="md-icon material-icons">edit</i>;
  const deleteButton = () => <i className="md-icon material-icons">delete</i>;
  const editColumns = [
    {
      key: EDIT_COLUMN_KEY,
      name: "Edit",
      width: 60,
      dataSource: editButton,
      dataType: DATA_TYPE_BUTTON,
      resizable: false,
      tooltip: "Click to edit notification",
    },
    {
      key: DELETE_COLUMN_KEY,
      name: "Delete",
      width: 60,
      dataSource: deleteButton,
      dataType: DATA_TYPE_BUTTON,
      resizable: false,
      tooltip: "Click to delete notification",
    },
  ];
  if (props.canEdit) {
    columns.unshift(...editColumns);
  }

  // Grid Actions

  const dialogProps = { className: "add-edit-stamp-dialog", width: 550 };

  const onAdd = () => {
    const maxSort = stamps.reduce((acc, s) => s.sortOrder > acc ? s.sortOrder : acc, 0);
    const newStamp: CustomerSurveyTypeStampDto = {
      stampId: null,
      completeFormId: null,
      text: "",
      sortOrder: maxSort + 1,
    };

    dialog.show(
      "Add Stamp",
      <AddEditStamp
        onSave={onAddFinished}
        onCancel={dialog.hide}
        stamp={newStamp}
        />,
        null,
        dialogProps
    );
  };

  const onAddFinished = (stamp: CustomerSurveyTypeStampDto) => {
    addSurveyTypeStamp(props.customerDetails.number, surveyTypeCode, stamp, getStamps, mounted);
    dialog.hide();
  };

  const onEditTextFinished = (stamp: CustomerSurveyTypeStampDto) => {
    editSurveyTypeStamp(props.customerDetails.number, surveyTypeCode, stamp, getStamps, mounted);
    dialog.hide();
  };

  const onConfirmDelete = (selectedOriginalIndex: number) => {
    const stampId = stamps[selectedOriginalIndex].stampId;
    removeSurveyTypeStamp(props.customerDetails.number, surveyTypeCode, stampId, getStamps, mounted);
    dialog.hide();
  };

  const handleGridButtons = (selectedOriginalIndex: number, columnKey: string) => {
    if (props.canEdit) {
      switch (columnKey) {
        case EDIT_COLUMN_KEY:
          dialog.show(
            "Edit Stamp",
            <AddEditStamp
              onSave={onEditTextFinished}
              onCancel={dialog.hide}
              stamp={stamps[selectedOriginalIndex]}
              />,
              null,
              dialogProps
          );
          break;
        case DELETE_COLUMN_KEY:
          dialog.showYesNo(
            "Confirm Delete Stamp",
            "Do you want to delete this stamp? This action cannot be undone.",
            () => onConfirmDelete(selectedOriginalIndex),
            dialog.hide,
            "Delete",
            "Cancel");
          break;
      }
    }
  };

  // Render

  return (
    <MGrid
      configurationId="CustomerSurveyTypeStamps"
      headerText="Stamps"
      isFetchingData={isLoading || !surveyTypeCode || !props.customerDetails}
      columnDefinitions={columns}
      data={stamps}
      disableFilters
      onCellButtonClick={handleGridButtons}
      displayNumOfItemsFound={true}
      actions={props.canEdit ? [{ code: ACTION_ADD, onClick: onAdd }] : null}
      cardStyle
      minHeight={300}
    />
  );
};

const mapStateToProps = state => {
  return {
    selectedSurveyType: state.customer.selectedSurveyType,
    customerDetails: state.customer.customerDetails,
    canEdit: userHasRight(CAN_EDIT_STAMPS)(state),
  };
};

export default connect(mapStateToProps)(CustomerSurveyTypeStamps);